import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import styled, { prop } from '@nobia/zeus-components/lib/styled'

import { Icon } from '../icons'
import { formatPrice } from '../cards/utils'
import { useTranslation } from '../../utils/useTranslation'
import {
  getMonthlyPayment,
  getTotalPayment,
  getDeposit,
  getYearsToPay,
} from '../../brands/monthsToPay'

const Overlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 20;
  height: 100%;
  backdrop-filter: blur(4px);
  background: rgba(18, 33, 38, 0.1);
  opacity: ${(p) => `${p.opacity}`};
  transition: opacity ease 0.5s;
`

const Modal = styled.div`
  position: sticky;
  bottom: 0;
  top: ${(p) => `${p.top}%`};
  min-height: 40vh;
  width: 100%;
  background: #fff;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  z-index: 40;
  text-align: left;
  padding: ${prop('theme.body.padding')};
  opacity: ${(p) => `${p.opacity}`};
  transition: opacity ease 0.5s;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    position: sticky;
    top: 50%;
    left: 50%;
    padding: 20px ${prop('theme.body.padding')};
    bottom: auto;
    width: 50%;
    max-width: 600px;
    min-height: 40vh;
    border-radius: 10px;
    transform: translate(-50%, -50%);
  }
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    padding: 20px 40px;
  }
`

const H3 = styled.h3`
  font-size: 21px;
  margin: 10px 0 0;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    font-size: 24px;
  }
`

const H4 = styled.h4`
  display: ${(p) => (p.isPayLater ? 'flex' : 'none')};
  width: fit-content;
  font-size: 16px;
  margin: 25px auto;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    font-size: 18px;
  }
`

const P = styled.p`
  margin-top: 0;
  font-size: 16px;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    margin-bottom: 30px;
  }
`

const Cost = styled.div`
  border-top: 1px solid #eceded;
`

const CostDetail = styled.p`
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  margin-top: 14px;
`

const CostWrapper = styled.div`
  border-top: ${(p) => (p.isPayLater ? '1px solid #ECEDED' : 'none')};
`

const Price = styled.span`
  font-weight: 400;
  font-family: 'Campton';
`

const MonthlyPayment = styled(CostDetail)`
  display: flex;
  align-items: baseline;
  font-size: 16px;
  border-top: 1px solid #eceded;
  border-bottom: 1px solid #eceded;
  padding: 12px 0 10px;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    font-size: 18px;
  }
`

const Strong = styled.strong`
  font-family: ${prop('theme.font.headings.fontFamily')};
  font-size: 21px;
  font-weight: 500;
`

const Close = styled.button`
  position: absolute;
  top: 25px;
  right: 10px;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    right: 30px;
  }
`

const BadaButton = styled.a`
  display: block;
  text-decoration: none;
  text-align: center;
  margin: 20px auto 0;
  border-radius: 3px;
  padding: 15px;
  font-size: 16px;
  font-weight: 500;
  width: 100%;
  cursor: pointer;
  background-color: ${(p) => (p.disable ? '#DDDEDF' : p.theme.colors.button)};
  color: ${(p) => (p.disable ? '#7F8487' : p.theme.colors.white)};
  transition: opacity 0.5s;
  &:hover {
    opacity: 0.8;
  }
`

const FinanceOverlay = ({
  setShowModal,
  summary,
  financeId,
  financeTitle,
  interestAPR,
  months,
}) => {
  const { t, country } = useTranslation()
  const overlayRef = useRef()
  const modalRef = useRef()
  const [opacity, setOpacity] = useState(0)
  const [top, setTop] = useState(0)

  const onClose = (e) => {
    if (overlayRef.current === e.target) {
      setShowModal(false)
    }
  }

  const setModalTop = () => {
    const viewportHeight = window.innerHeight
    const modalHeight = modalRef.current.offsetHeight
    const percentageHeight = (modalHeight / viewportHeight) * 100
    setTop(100 - percentageHeight)
  }

  useEffect(() => {
    setModalTop()
    setOpacity(1)
    window.addEventListener('resize', setModalTop)
    return () => window.removeEventListener('resize', setModalTop)
  }, [])

  return (
    <Overlay ref={overlayRef} onClick={onClose} opacity={opacity}>
      <Modal ref={modalRef} top={top} opacity={opacity}>
        <H3>{financeTitle}</H3>
        <P>
          {getYearsToPay(months)}{' '}
          {t('app.components.finance-selection.modal.payment-term')}
        </P>
        <Close onClick={() => setShowModal(false)}>
          <Icon name="x" width="20px" height="20px" />
        </Close>
        <Cost>
          <CostDetail>
            {t('app.components.finance-selection.modal.price')}
            <Price>{formatPrice({ price: summary, country })}</Price>
          </CostDetail>
          <CostDetail>
            {t('app.components.finance-selection.modal.deposit')}
            <Price>
              {formatPrice({
                price: getDeposit(summary, months, financeId),
                country,
              })}
            </Price>
          </CostDetail>
          {financeId === 'BNPL' && (
            <CostDetail>
              {t('app.components.finance-selection.modal.amount-payable-year')}
              <Price>
                {formatPrice({
                  price: summary - getDeposit(summary, months, financeId),
                  country,
                })}
              </Price>
            </CostDetail>
          )}
          {financeId !== 'IFC' && (
            <CostWrapper isPayLater={financeId === 'BNPL'}>
              <H4 isPayLater={financeId === 'BNPL'}>
                {t(
                  'app.components.finance-selection.modal.monthly-instalments'
                )}
              </H4>
              <CostDetail>
                {t('app.components.finance-selection.modal.amount-payable')}
                <Price>
                  {formatPrice({
                    price: getTotalPayment(summary, months, financeId),
                    country,
                  })}
                </Price>
              </CostDetail>
              <CostDetail>
                {t('app.components.finance-selection.modal.amount-credit')}
                <Price>
                  {formatPrice({
                    price:
                      getTotalPayment(summary, months, financeId) -
                      (summary - getDeposit(summary, months, financeId)),
                    country,
                  })}
                </Price>
              </CostDetail>
            </CostWrapper>
          )}
          <CostDetail>
            {t('app.components.finance-selection.modal.APR')}
            <Price>{interestAPR}%</Price>
          </CostDetail>
        </Cost>
        <MonthlyPayment>
          {t('app.components.finance-selection.modal.monthlyPayment')}
          <Strong>
            {formatPrice({
              price: getMonthlyPayment(summary, months, financeId),
              country,
              digits: 2,
            })}
          </Strong>
        </MonthlyPayment>
        <BadaButton href={t('app.bada.call-to-action.link')} target="_blank">
          {t('app.components.finance-selection.modal.call-to-action')}
        </BadaButton>
      </Modal>
    </Overlay>
  )
}

FinanceOverlay.propTypes = {
  summary: PropTypes.number.isRequired,
  months: PropTypes.number.isRequired,
  interestAPR: PropTypes.number.isRequired,
  financeTitle: PropTypes.string.isRequired,
  financeId: PropTypes.string.isRequired,
  setShowModal: PropTypes.func.isRequired,
}

export default FinanceOverlay
